'use client';

import { FC, useRef } from 'react';

import { FEATURE_FLAGS } from '@telialt/common-cms/src/CustomJson/constants';
import { useHybrisConfiguration } from '@telialt/common-cms/src/CustomJson/hooks/useHybrisConfiguration';
import { MagnifyingGlass, ZoomProvider } from '@telialt/common-gallery/src/ZoomProvider';
import { ToastContainer } from '@telialt/common-toast/src';
import { VasModal } from '@telialt/core-eshop/src/CartSummaryBox/ValueAddedServices/VasModal';
import { ClientSideError } from '@telialt/core-eshop/src/components/ClientSideError/ClientSideError';
import ErrorBoundary from '@telialt/core-eshop/src/components/ErrorBoundary/ErrorBoundary';
import { useSendGAEventProductView } from '@telialt/core-eshop/src/hooks';
import { useDeleteCouponMutation } from '@telialt/core-eshop/src/hooks/mutations/useDeleteCouponMutation';
import { useCartQuery } from '@telialt/core-eshop/src/hooks/queries/useCartQuery';
import { useSelectedProductVariant } from '@telialt/core-eshop/src/hooks/useSelectedProductVariant';
import {
    ProductVariationBadges,
    useAddInitialProductToCart,
} from '@telialt/core-eshop/src/product/ProductVariationBadges';
import { VideolyWidget } from '@telialt/core-eshop/src/product/VideolyWidget';
import { ProductStockStatus } from '@telialt/core-eshop/src/ProductStockStatus/ProductStockStatus';
import { ESHOP_TEST_ID } from '@telialt/core-eshop/src/test/test-ids';
import { SessionProlongationModal } from '@telialt/core-nextjs/src/components/SessionProlongationModal/SessionProlongationModal';
import { selectIsCartFresh } from '@telialt/utils/src/eshopSelectors';
import '@telialt/utils/src/polyfills/string-replace-all';
import { TeliaSegment } from '@telialt/utils/src/segment';

import { useBaseProductFromUrlParam } from '../../hooks/useBaseProductFromUrlParam';
import { useSelectContractTypeFromQuery } from '../../hooks/useSelectContractTypeFromQuery';
import { DescriptionChecklist } from './DescriptionChecklist/DescriptionChecklist';
import { EnergyEfficiencyInfo } from './EnergyEfficiencyInfo/EnergyEfficiencyInfo';
import { ESimInfoCard } from './ESimInfoCard/ESimInfoCard';
import { ESimNotice } from './ESimNotice/ESimNotice';
import { Badges, MarketingTag } from './GalleryBadges';
import { GalleryContainer } from './GalleryContainer';
import { GiftDetailsModal } from './GiftDetailsModal/GiftDetailsModal';
import { GiftSelector } from './GiftSelector';
import { GoBackButton } from './GoBackButton';
import { AskToUnRestrictLeasingModal } from './LeasingPlanSelector';
import { LocalCodes } from './LocalCodes/LocalCodes';
import { PaymentOptionsSelectors } from './PaymentOptionsSelectors/PaymentOptionsSelectors';
import { DescriptionSection, DividerGrid, PageGrid, Title } from './ProductDetailsPage.styles';
import { ProductDetailsSkeleton } from './ProductDetailsSkeleton/ProductDetailsSkeleton';
import { ProductInsurance } from './ProductInsurance/ProductInsurance';
import { ProductLoginCta } from './ProductLoginCta/ProductLoginCta';
import { ProductOrderCart } from './ProductOrderCart/ProductOrderCart';
import { ProductOverviewHandle } from './ProductOverview/ProductOverview';
import { ProductStickySummary } from './ProductStickySummary/ProductStickySummary';
import { SpecialOfferGiftModal, SpecialOffersBlock, SpecialOfferSingleModal } from './SpecialOffers';
import { Summary } from './Summary/Summary';
import { TopPrice } from './TopPrice';

interface ProductDetailsPageProps {
    segment: TeliaSegment;
}

export const ProductDetailsPage: FC<ProductDetailsPageProps> = ({ segment }) => {
    const productVariant = useSelectedProductVariant();
    const { baseProduct } = useBaseProductFromUrlParam();
    useAddInitialProductToCart(baseProduct?.variantMatrix);
    const { isSelectContractTypeLoading } = useSelectContractTypeFromQuery();
    const { cart } = useCartQuery();
    const { isDeleteCouponLoading } = useDeleteCouponMutation();
    const { isLoading: isFeatureFlagLoading } = useHybrisConfiguration(FEATURE_FLAGS.PURCHASE_DISABLED);

    const productOverviewRef = useRef<ProductOverviewHandle & HTMLElement>(null);
    const productImageData = productVariant?.images;

    const topPriceRef = useRef<HTMLElement | null>(null);
    const cartTotalsRef = useRef<HTMLElement | null>(null);

    useSendGAEventProductView();

    if (
        !cart ||
        selectIsCartFresh(cart) ||
        !baseProduct ||
        !productVariant ||
        isDeleteCouponLoading ||
        isFeatureFlagLoading ||
        isSelectContractTypeLoading
    ) {
        return <ProductDetailsSkeleton />;
    }

    return (
        <>
            <PageGrid data-test={segment === 'b2b' ? ESHOP_TEST_ID.B2B_PRODUCT_PAGE : ESHOP_TEST_ID.B2C_PRODUCT_PAGE}>
                <GoBackButton goBackUrl={productVariant?.categoryUrl || baseProduct?.categoryUrl} />
                <ZoomProvider>
                    <DividerGrid>
                        <GalleryContainer
                            productImageData={productImageData}
                            isZoomEnabled={true}
                            renderBadges={() => <Badges badges={baseProduct?.badges} />}
                            renderMarketingTag={() => <MarketingTag marketingTag={baseProduct?.marketingTag} />}
                            renderVideoly={() => <VideolyWidget baseProductCode={baseProduct?.code} />}
                        />
                        <div data-test={ESHOP_TEST_ID.INTERACTION_PANEL}>
                            <MagnifyingGlass />
                            <Title data-test={ESHOP_TEST_ID.ESHOP_PRODUCT_PAGE_TITLE}>{baseProduct?.name}</Title>
                            <TopPrice ref={topPriceRef} />
                            <LocalCodes />
                            {productVariant?.energyConsumingSupported && (
                                <EnergyEfficiencyInfo
                                    energyClass={productVariant?.energyClass}
                                    energyConsumingSupported={productVariant?.energyConsumingSupported}
                                    energyLabel={productVariant?.energyLabel}
                                    productInformationSheet={productVariant?.productInformationSheet}
                                />
                            )}
                            <Summary />
                            <DescriptionChecklist />
                            <ProductStockStatus />
                            <ProductLoginCta />
                            <ESimInfoCard />
                            <SpecialOffersBlock />
                            <ProductVariationBadges variantMatrix={baseProduct?.variantMatrix} />
                            <PaymentOptionsSelectors />
                            <ProductInsurance />
                            <GiftSelector />
                            <ESimNotice productType={baseProduct?.productType} brand={baseProduct?.brand} />
                            <ProductOrderCart cartTotalsRef={cartTotalsRef} />
                            <ProductStickySummary
                                topPriceRef={topPriceRef}
                                cartTotalsRef={cartTotalsRef}
                                baseProduct={baseProduct}
                            />
                        </div>
                    </DividerGrid>
                </ZoomProvider>
                <ErrorBoundary ErrorComponent={ClientSideError}>
                    <DescriptionSection
                        ref={productOverviewRef}
                        productVariant={productVariant}
                        isRetailsShown={true}
                        data-test={ESHOP_TEST_ID.ESHOP_PRODUCT_PAGE_DESCRIPTION_SECTION}
                    />
                </ErrorBoundary>
                <VasModal />
                <SpecialOfferGiftModal />
                <SpecialOfferSingleModal />
                <GiftDetailsModal />
                <AskToUnRestrictLeasingModal />
                <SessionProlongationModal />
            </PageGrid>
            <ToastContainer />
        </>
    );
};
